import { SilentRequest } from "@azure/msal-browser";
import {
  ApimSubscriptionKeyResponse,
  IDTokenRepsonse,
  UserInfo,
} from "../types/auth";
import {
  APIM_BASE_URL,
  TeamsGraphTokenKeyPrefix,
  TeamsIdTokenKeyPrefix,
} from "../constants/auth";
import { authentication } from "@microsoft/teams-js";
import { loginRequest, msalInstance } from "../auth";
import { checkInTeams } from "../services/teams";
import {
  GetFromSessionStorage,
  SetInSessionStorage,
} from "../services/storage";
import { decodeJwtIdToken, isTokenExpired } from "../services/jwt";
import axios from "axios";

export function getTeamsIdToken(tenantId: string) {
  return new Promise<string | null>((resolve, reject) => {
    (async () => {
      const cacheKey = `${TeamsIdTokenKeyPrefix}_${tenantId}`;
      const token = GetFromSessionStorage(cacheKey);
      if (token === null || isTokenExpired(token)) {
        const authTokenRequestParams: authentication.AuthTokenRequestParameters =
          {};
        const idToken = await authentication.getAuthToken(
          authTokenRequestParams
        );

        SetInSessionStorage(cacheKey, idToken);
        resolve(idToken);
      } else {
        resolve(token);
      }
    })();
  });
}

export function getTeamsAccessToken(tenantId: string) {
  return new Promise<string | null>((resolve, reject) => {
    (async () => {
      const cacheKey = `${TeamsGraphTokenKeyPrefix}_${tenantId}`;
      const token = GetFromSessionStorage(cacheKey);
      if (token === null || isTokenExpired(token)) {
        const authTokenRequestParams: authentication.AuthTokenRequestParameters =
          {
            resources: ["https://graph.microsoft.com"],
          };
        const accessToken = await authentication.getAuthToken(
          authTokenRequestParams
        );

        SetInSessionStorage(cacheKey, accessToken);
        resolve(accessToken);
      } else {
        resolve(token);
      }
    })();
  });
}

export async function getIdToken(
  tenantId: string,
  silentRequest?: SilentRequest
) {
  const isInTeams = checkInTeams();
  if (isInTeams) {
    const idToken = await getTeamsIdToken(tenantId);
    return idToken;
  }

  let req: SilentRequest = {
    account: msalInstance.getAllAccounts()[0],
    scopes: loginRequest.scopes,
  };

  if (silentRequest) {
    req = {
      ...req,
      ...silentRequest,
      forceRefresh: true,
    };
  }

  const authTokenResult = await msalInstance.acquireTokenSilent(req);
  return authTokenResult?.idToken;
}

export async function getAccessToken(
  tenantId: string,
  silentRequest?: SilentRequest
) {
  const isInTeams = checkInTeams();
  if (isInTeams) {
    const accessToken = await getTeamsAccessToken(tenantId);
    return accessToken;
  }

  let req: SilentRequest = {
    account: msalInstance.getAllAccounts()[0],
    scopes: loginRequest.scopes,
  };

  if (silentRequest) {
    req = {
      ...req,
      ...silentRequest,
      forceRefresh: true,
    };
  }

  const authTokenResult = await msalInstance.acquireTokenSilent(req);
  return authTokenResult?.accessToken;
}

export async function getUserDetails(
  tenantId: string,
  silentRequest?: SilentRequest
): Promise<UserInfo | undefined> {
  const tokenDetails: string | null = await getIdToken(tenantId, silentRequest);
  const decodedToken: IDTokenRepsonse | null = decodeJwtIdToken(
    tokenDetails ?? ""
  );
  const isInTeams = checkInTeams();
  if (isInTeams) {
    return {
      type: "teamsid",
      userEmail: decodedToken?.preferred_username || "",
      userName: decodedToken?.preferred_username || "",
      id: decodedToken?.sub || "",
      firstName: decodedToken ? decodedToken.name ?? "" : "",
      lastName: decodedToken ? decodedToken.name ?? "" : "",
    } as UserInfo;
  } else {
    return {
      type: "msal",
      userEmail: decodedToken?.preferred_username || "",
      userName: decodedToken?.preferred_username || "",
      id: decodedToken?.sub || "",
      firstName: decodedToken ? decodedToken.name ?? "" : "",
      lastName: decodedToken ? decodedToken.name ?? "" : "",
    } as UserInfo;
  }
}

export async function getAPIMSubscriptionKey(
  token: string
): Promise<ApimSubscriptionKeyResponse> {
  const response = await axios.get<ApimSubscriptionKeyResponse>(
    `${APIM_BASE_URL}/GetAPIMSubscriptionKey`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
}

export async function getIdTokenNoCache(silentRequest?: SilentRequest) {
  const isInTeams = checkInTeams();
  if (isInTeams) {
    const authTokenRequestParams: authentication.AuthTokenRequestParameters =
      {};
    const idToken = await authentication.getAuthToken(authTokenRequestParams);

    return idToken;
  }

  let req: SilentRequest = {
    account: msalInstance.getAllAccounts()[0],
    scopes: loginRequest.scopes,
  };

  if (silentRequest) {
    req = {
      ...req,
      ...silentRequest,
      forceRefresh: true,
    };
  }

  const authTokenResult = await msalInstance.acquireTokenSilent(req);
  return authTokenResult?.idToken;
}

export default {
  getIdToken,
  getUserDetails,
  getAccessToken,
};
